import React, { useState } from 'react';

const roles = ['Investor', 'Sponsor', 'Press', 'Other'];

export default function ContactForm({ onSuccess }) {
  const [form, setForm] = useState({ name: '', organisation: '', email: '', role: '', message: '' });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const validate = () => {
    const errs = {};
    if (!form?.name?.trim()) errs.name = 'Name is required';
    if (!form?.organisation?.trim()) errs.organisation = 'Organisation is required';
    if (!form?.email?.trim()) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(form?.email)) errs.email = 'Please enter a valid email address';
    if (!form?.role) errs.role = 'Please select a role';
    if (!form?.message?.trim()) errs.message = 'Message is required';
    return errs;
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors?.[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const errs = validate();
    if (Object.keys(errs)?.length > 0) {
      setErrors(errs);
      return;
    }
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 800));
    setSubmitting(false);
    setForm({ name: '', organisation: '', email: '', role: '', message: '' });
    onSuccess?.();
  };

  const inputClass = (field) =>
    `w-full px-4 py-3 rounded-md border text-sm font-body transition-colors focus:outline-none focus:ring-2 ${
      errors?.[field]
        ? 'border-red-300 focus:ring-red-200' :'border-gray-200 focus:ring-blue-100 focus:border-blue-300'
    }`;

  return (
    <div>
      <h2 className="font-heading text-2xl font-semibold mb-2" style={{ color: 'var(--color-primary)' }}>Send us a message</h2>
      <p className="text-sm text-gray-500 font-body mb-8">We respond to all enquiries within one business day.</p>
      <form onSubmit={handleSubmit} noValidate className="space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-xs font-caption font-medium text-gray-700 mb-1.5">Name <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={form?.name}
              onChange={(e) => handleChange('name', e?.target?.value)}
              placeholder="Your full name"
              className={inputClass('name')}
            />
            {errors?.name && <p className="mt-1 text-xs text-red-500 font-caption">{errors?.name}</p>}
          </div>
          <div>
            <label className="block text-xs font-caption font-medium text-gray-700 mb-1.5">Organisation <span className="text-red-400">*</span></label>
            <input
              type="text"
              value={form?.organisation}
              onChange={(e) => handleChange('organisation', e?.target?.value)}
              placeholder="Your organisation"
              className={inputClass('organisation')}
            />
            {errors?.organisation && <p className="mt-1 text-xs text-red-500 font-caption">{errors?.organisation}</p>}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label className="block text-xs font-caption font-medium text-gray-700 mb-1.5">Email address <span className="text-red-400">*</span></label>
            <input
              type="email"
              value={form?.email}
              onChange={(e) => handleChange('email', e?.target?.value)}
              placeholder="you@organisation.com"
              className={inputClass('email')}
            />
            {errors?.email && <p className="mt-1 text-xs text-red-500 font-caption">{errors?.email}</p>}
          </div>
          <div>
            <label className="block text-xs font-caption font-medium text-gray-700 mb-1.5">Role <span className="text-red-400">*</span></label>
            <select
              value={form?.role}
              onChange={(e) => handleChange('role', e?.target?.value)}
              className={inputClass('role')}
              style={{ background: 'white' }}
            >
              <option value="">Select your role</option>
              {roles?.map((r) => (
                <option key={r} value={r}>{r}</option>
              ))}
            </select>
            {errors?.role && <p className="mt-1 text-xs text-red-500 font-caption">{errors?.role}</p>}
          </div>
        </div>

        <div>
          <label className="block text-xs font-caption font-medium text-gray-700 mb-1.5">Message <span className="text-red-400">*</span></label>
          <textarea
            value={form?.message}
            onChange={(e) => handleChange('message', e?.target?.value)}
            placeholder="Tell us about your requirements or questions..."
            rows={5}
            className={inputClass('message')}
          />
          {errors?.message && <p className="mt-1 text-xs text-red-500 font-caption">{errors?.message}</p>}
        </div>

        <button
          type="submit"
          disabled={submitting}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-medium font-caption text-white transition-all duration-200 hover:opacity-90 disabled:opacity-60"
          style={{ background: 'var(--color-primary)' }}
        >
          {submitting ? (
            <>
              <svg className="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"/>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/>
              </svg>
              Sending...
            </>
          ) : (
            <>
              Submit enquiry
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M5 12h14M12 5l7 7-7 7"/>
              </svg>
            </>
          )}
        </button>
      </form>
    </div>
  );
}
